import { assert, expect } from 'chai';
import { wait } from '../../utils';
import constants from '../../constants';
import {
  APP_CHANNEL_AND_BROADCAST,
  APP_CHANNEL_AND_BROADCAST_TWICE,
  APP_CHANNEL_AND_BROADCAST_THRICE,
  APP_CHANNEL_AND_BROADCAST_TWICE_THEN_CLEAR,
  APP_CHANNEL_AND_BROADCAST_TWICE_THEN_CLEAR_TYPE,
} from '../support/channel-control';
import { ChannelControlImpl } from '../support/channels-support';
import { ChannelError, Context, FDC3ContextClearedEvent, getAgent } from '@finos/fdc3';
import { APIDocumentation } from '../support/apiDocuments';
import { expectChannelError } from '../support/error-support';

const documentation = '\r\nDocumentation: ' + APIDocumentation.desktopAgent + '\r\nCause:';

export default async () => {
  const fdc3 = await getAgent();
  const cc = new ChannelControlImpl(fdc3);

  return describe('fdc3.appChannels', () => {
    beforeEach(cc.leaveChannel);

    afterEach(async function afterEach() {
      await cc.closeMockAppIfOpened(this.currentTest!.title);
    });

    it('(ChannelErrorMalformedContext) Should reject channel broadcast with MalformedContext for an invalid context', async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- Retrieve an app channel\r\n- Broadcast a context object without a string type${documentation}`;
      const channel = await fdc3.getOrCreateChannel('fdc3.conformance.channel-errors');

      await expectChannelError(() => channel.broadcast({} as Context), ChannelError.MalformedContext, errorMessage);
    });

    it('(ChannelErrorInvalidArguments) Should reject getOrCreateChannel with InvalidArguments for a non-string channel id', async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- Call getOrCreateChannel with a non-string channel id${documentation}`;

      await expectChannelError(
        () => fdc3.getOrCreateChannel(null as unknown as string),
        ChannelError.InvalidArguments,
        errorMessage
      );
    });

    const acTestId =
      '(ACBasicUsage1) Should receive context when app a adds a listener and app B broadcasts to the same app channel';
    it(acTestId, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds adds a context listener of type null\r\n- App B retrieves the same app channel as A\r\n- App B broadcasts context of type fdc3.instrument${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId);
      let receivedContext = false;
      const listener1 = await cc.setupAndValidateListener(testChannel, null, 'fdc3.instrument', errorMessage, () => {
        receivedContext = true;
      });
      await cc.openChannelApp(acTestId, testChannel.id, APP_CHANNEL_AND_BROADCAST);
      await resolveExecutionCompleteListener;
      try {
        if (!receivedContext) {
          //allow upto a second for the context to arrive
          await wait(constants.ShortWait);
          if (!receivedContext) {
            assert.fail(`No context received!\n${errorMessage}`);
          }
        }
      } finally {
        cc.unsubscribeListeners([listener1]);
      }
    });

    const acTestId2 =
      '(ACBasicUsage2) Should receive context when app B broadcasts context to an app channel before A retrieves current context';
    it(acTestId2, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A & B retrieve the same app channel\r\n- App B broadcasts context of type fdc3.instrument\r\n- App A retrieves current context of type null${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId2);
      await cc.openChannelApp(acTestId2, testChannel.id, APP_CHANNEL_AND_BROADCAST);
      await resolveExecutionCompleteListener;
      let receivedContext = false;
      await cc.setupContextChecker(testChannel, null, 'fdc3.instrument', errorMessage, () => (receivedContext = true));

      if (!receivedContext) {
        //allow upto a second for the context to arrive
        await wait(constants.ShortWait);
        if (!receivedContext) {
          assert.fail(`No context received!\n${errorMessage}`);
        }
      }
    });

    const acClearContext1 =
      '(ACClearContext1) Should receive a contextCleared event and null current context when app B clears all context on the same app channel';
    it(acClearContext1, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App A adds a contextCleared event listener\r\n- App B retrieves the same app channel and broadcasts fdc3.instrument and fdc3.contact\r\n- App B clears all context on the channel${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acClearContext1);
      let receivedContext = false;
      const clearedEvents: FDC3ContextClearedEvent[] = [];

      const contextListener = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'fdc3.instrument',
        errorMessage,
        () => {
          receivedContext = true;
        }
      );
      const eventListener = await cc.setupContextClearedEventListener(testChannel, errorMessage, event => {
        clearedEvents.push(event);
      });

      await cc.openChannelApp(acClearContext1, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE_THEN_CLEAR);
      await resolveExecutionCompleteListener;
      try {
        if (!receivedContext) {
          await wait(constants.ShortWait);
        }
        if (clearedEvents.length === 0) {
          await wait(constants.ShortWait);
        }
        assert.isTrue(receivedContext, `No context received!\n${errorMessage}`);
        assert.isNotEmpty(clearedEvents, `No contextCleared event received!\n${errorMessage}`);
        const clearedEvent = clearedEvents[0];
        expect(clearedEvent.details.channelId, errorMessage).to.be.equals(testChannel.id);
        expect(clearedEvent.details.contextType, errorMessage).to.be.null;

        const currentInstrument = await testChannel.getCurrentContext('fdc3.instrument');
        expect(currentInstrument, `getCurrentContext should return null for a cleared type\n${errorMessage}`).to.be
          .null;
      } finally {
        cc.unsubscribeListeners([contextListener, eventListener]);
      }
    });

    const acClearContext2 =
      '(ACClearContext2) Should receive a contextCleared event identifying the cleared type when app B clears a specific type on the same app channel';
    it(acClearContext2, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App A adds a contextCleared event listener\r\n- App B retrieves the same app channel and broadcasts fdc3.instrument and fdc3.contact\r\n- App B clears the fdc3.instrument context type on the channel${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acClearContext2);
      let receivedContext = false;
      const clearedEvents: FDC3ContextClearedEvent[] = [];

      const contextListener = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'fdc3.instrument',
        errorMessage,
        () => {
          receivedContext = true;
        }
      );
      const eventListener = await cc.setupContextClearedEventListener(testChannel, errorMessage, event => {
        clearedEvents.push(event);
      });

      await cc.openChannelApp(acClearContext2, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE_THEN_CLEAR_TYPE);
      await resolveExecutionCompleteListener;
      try {
        if (!receivedContext) {
          await wait(constants.ShortWait);
        }
        if (clearedEvents.length === 0) {
          await wait(constants.ShortWait);
        }
        assert.isTrue(receivedContext, `No context received!\n${errorMessage}`);
        assert.isNotEmpty(clearedEvents, `No contextCleared event received!\n${errorMessage}`);
        const clearedEvent = clearedEvents[0];
        expect(clearedEvent.details.channelId, errorMessage).to.be.equals(testChannel.id);
        expect(clearedEvent.details.contextType, errorMessage).to.be.equals('fdc3.instrument');

        const currentInstrument = await testChannel.getCurrentContext('fdc3.instrument');
        expect(currentInstrument, `getCurrentContext should return null for a cleared type\n${errorMessage}`).to.be
          .null;
      } finally {
        cc.unsubscribeListeners([contextListener, eventListener]);
      }
    });

    const acTestId4 =
      '(ACFilteredContext1) Should only receive the listened context when app B broadcasts multiple contexts to the same app channel';
    it(acTestId4, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App B retrieves the same app channel as A\r\n- App B broadcasts a context of type fdc3.instrument and fdc3.contact${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId4);
      let receivedContext = false;
      const listener1 = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'fdc3.instrument',
        errorMessage,
        () => {
          receivedContext = true;
        }
      );
      await cc.openChannelApp(acTestId4, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE);
      await resolveExecutionCompleteListener;
      try {
        if (!receivedContext) {
          //allow upto a second for the context to arrive
          await wait(constants.ShortWait);
          if (!receivedContext) {
            assert.fail(`No context received!\n${errorMessage}`);
          }
        }
      } finally {
        cc.unsubscribeListeners([listener1]);
      }
    });

    const acTestId6 =
      '(ACUnsubscribe) Should not receive context when unsubscribing an app channel before app B broadcasts to that channel';
    it(acTestId6, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App A unsubscribes the app channel\r\n- App B retrieves the same app channel\r\n- App B broadcasts a context of type fdc3.instrument and fdc3.contact${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId6);

      const listener = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'unexpected-context',
        errorMessage,
        () => {
          /*noop*/
        }
      );
      await cc.unsubscribeListeners([listener]);
      await cc.openChannelApp(acTestId6, testChannel.id, APP_CHANNEL_AND_BROADCAST);

      await resolveExecutionCompleteListener;
    });

    const acTestId7 =
      '(ACFilteredContext2) Should not receive context when app B broadcasts context to a different app channel';
    it(acTestId7, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App B retrieves a different app channel\r\n- App B broadcasts a context of type fdc3.instrument${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const listener = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'unexpected-context',
        errorMessage,
        () => {
          /*noop*/
        }
      );
      const differentTestChannel = await cc.createRandomTestChannel();
      await cc.openChannelApp(acTestId7, differentTestChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE);
      await wait(constants.ShortWait); // give listener time to receive context
      cc.unsubscribeListeners([listener]);
    });

    const acTestId8 =
      '(ACFilteredContext3) Should not receive context when retrieving two different app channels before app B broadcasts the listened type to the first channel that was retrieved';
    it(acTestId8, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A switches to a different app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App B retrieves the first channel that A retrieved\r\n- App B broadcasts a context of type fdc3.instrument${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      let receivedContext = false;

      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId8);
      const differentAppChannel = await cc.createRandomTestChannel();
      const listener = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'fdc3.instrument',
        errorMessage,
        () => {
          receivedContext = true;
        }
      );
      const listener2 = await cc.setupAndValidateListener(
        differentAppChannel,
        'fdc3.instrument',
        'unexpected-context',
        errorMessage,
        () => {
          /*noop*/
        }
      );
      await cc.openChannelApp(acTestId8, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE);
      await resolveExecutionCompleteListener;
      try {
        if (!receivedContext) {
          //allow upto a second for the context to arrive
          await wait(constants.ShortWait);
          if (!receivedContext) {
            assert.fail(`No context received!\n${errorMessage}`);
          }
        }
      } finally {
        cc.unsubscribeListeners([listener, listener2]);
      }
    });

    const acTestId9 =
      '(ACFilteredContext4) Should not receive context when retrieving two different app channels before the second app broadcasts the listened type to the first channel that was retrieved';
    it(acTestId9, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A switches to a different app channel\r\n- App A adds a context listener of type fdc3.instrument\r\n- App B retrieves the first channel that A retrieved\r\n- App B broadcasts a context of type fdc3.instrument${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      let receivedContext = false;
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId9);
      const differentAppChannel = await cc.createRandomTestChannel();
      const listener = await cc.setupAndValidateListener(
        testChannel,
        'fdc3.instrument',
        'fdc3.instrument',
        errorMessage,
        () => {
          receivedContext = true;
        }
      );
      const listener2 = await cc.setupAndValidateListener(
        differentAppChannel,
        'fdc3.instrument',
        'unexpected-context',
        errorMessage,
        () => {
          /*noop*/
        }
      );
      await cc.openChannelApp(acTestId9, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE);
      await resolveExecutionCompleteListener;
      try {
        if (!receivedContext) {
          //allow upto a second for the context to arrive
          await wait(constants.ShortWait);
          if (!receivedContext) {
            assert.fail(`No context received!\n${errorMessage}`);
          }
        }
      } finally {
        cc.unsubscribeListeners([listener, listener2]);
      }
    });

    const acTestId10 =
      '(ACContextHistoryTyped) Should receive both contexts when app B broadcasts both contexts to the same app channel and A gets current context for each type';
    it(acTestId10, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App B retrieves the same app channel\r\n- App B broadcasts a context of type fdc3.instrument and fdc3.contact\r\n- App A gets current context for types fdc3.instrument and fdc3.contact${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId10);

      await cc.openChannelApp(acTestId10, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE);
      await resolveExecutionCompleteListener;

      await cc.setupContextChecker(testChannel, 'fdc3.instrument', 'fdc3.instrument', errorMessage, context => {
        expect(context.name).to.be.equals('History-item-1', errorMessage);
      });
      await cc.setupContextChecker(testChannel, 'fdc3.contact', 'fdc3.contact', errorMessage, context => {
        expect(context.name).to.be.equals('History-item-1', errorMessage);
      });
    });

    const acTestId11 =
      '(ACContextHistoryMultiple) Should retrieve the last broadcast context item when app B broadcasts a context with multiple history items to the same app channel and A gets current context';
    it(acTestId11, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App B retrieves the same app channel\r\n- App B broadcasts two different contexts of type fdc3.instrument\r\n- App A gets current context for types fdc3.instrument${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId11);

      await cc.openChannelApp(acTestId11, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE, 2);
      await resolveExecutionCompleteListener;
      await cc.setupContextChecker(testChannel, 'fdc3.instrument', 'fdc3.instrument', errorMessage, context => {
        expect(context.name).to.be.equals('History-item-2', errorMessage);
      });
      await cc.setupContextChecker(testChannel, 'fdc3.contact', 'fdc3.contact', errorMessage, context => {
        expect(context.name).to.be.equals('History-item-2', errorMessage);
      });
    });

    const acTestId12 =
      '(ACContextHistoryLast) Should retrieve the last broadcast context item when app B broadcasts two different contexts to the same app channel and A gets current context';
    it(acTestId12, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App B retrieves the same app channel\r\n- App B broadcasts a context of type fdc3.instrument and fdc3.contact\r\n- App B gets current context with no filter applied${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(acTestId12);
      await cc.openChannelApp(acTestId12, testChannel.id, APP_CHANNEL_AND_BROADCAST_TWICE);
      await resolveExecutionCompleteListener;

      const context = await testChannel.getCurrentContext();

      if (context === null) {
        assert.fail('No Context retrieved', errorMessage);
      } else if (context.type === 'fdc3.instrument') {
        assert.fail('Did not retrieve last broadcast context from app B', errorMessage);
      } else {
        expect(context.type).to.be.equals('fdc3.contact', errorMessage);
      }
    });

    const ACArrayContextListeners1 =
      '(ACArrayContextListeners1) Should receive context for multiple types and reject non-matching types when using array-based addContextListener on app channel';
    it(ACArrayContextListeners1, async () => {
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A adds context listener for ["fdc3.instrument", "fdc3.contact"]\r\n- App B retrieves the same app channel\r\n- App B broadcasts fdc3.instrument, fdc3.contact, and fdc3.portfolio contexts\r\n- App A should receive instrument and contact but NOT portfolio${documentation}`;

      const testChannel = await cc.createRandomTestChannel();
      const resolveExecutionCompleteListener = cc.initCompleteListener(ACArrayContextListeners1);
      const receivedContextTypes: string[] = [];

      const listener = await cc.setupArrayContextListener(
        testChannel,
        ['fdc3.instrument', 'fdc3.contact'],
        ['fdc3.instrument', 'fdc3.contact'],
        errorMessage,
        (context: Context) => {
          receivedContextTypes.push(context.type);
        }
      );

      await cc.openChannelApp(ACArrayContextListeners1, testChannel.id, APP_CHANNEL_AND_BROADCAST_THRICE);
      await resolveExecutionCompleteListener;

      try {
        await wait(constants.ShortWait);
        // Should receive both instrument and contact
        expect(receivedContextTypes).to.include('fdc3.instrument', errorMessage);
        expect(receivedContextTypes).to.include('fdc3.contact', errorMessage);
        // Should NOT receive portfolio (non-matching type)
        expect(receivedContextTypes).to.not.include(
          'fdc3.portfolio',
          `Should NOT receive fdc3.portfolio context\n${errorMessage}`
        );
        // Should have received exactly 2 contexts (not 3)
        expect(receivedContextTypes.length).to.equal(
          2,
          `Should receive exactly 2 contexts (not portfolio), received: ${receivedContextTypes.join(', ')}`
        );
      } finally {
        cc.unsubscribeListeners([listener]);
      }
    });

    const ACArrayEmptyContext1 =
      '(ACArrayEmptyContext1) Should reject an empty array addContextListener on an app channel with InvalidArguments';
    it(ACArrayEmptyContext1, async () => {
      const testChannel = await cc.createRandomTestChannel();
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A calls addContextListener with an empty array\r\n- The promise should reject with ChannelError.InvalidArguments${documentation}`;

      await expectChannelError(
        () => testChannel.addContextListener([] as string[], () => {}),
        ChannelError.InvalidArguments,
        errorMessage
      );
    });

    const ACArrayNullContext1 =
      '(ACArrayNullContext1) Should reject an array containing null addContextListener on an app channel with InvalidArguments';
    it(ACArrayNullContext1, async () => {
      const testChannel = await cc.createRandomTestChannel();
      const errorMessage = `\r\nSteps to reproduce:\r\n- App A retrieves an app channel\r\n- App A calls addContextListener with ["fdc3.instrument", null]\r\n- The promise should reject with ChannelError.InvalidArguments${documentation}`;

      await expectChannelError(
        () => testChannel.addContextListener(['fdc3.instrument', null] as unknown as string[], () => {}),
        ChannelError.InvalidArguments,
        errorMessage
      );
    });
  });
};
